import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '@/components/layout';
import {
  Avatar,
  Badge,
  Spinner,
  ErrorState,
  Icon,
  Button,
} from '@/components/atoms';
import { ProfileFeed } from '@/components/domain';
import { specialists, favorites, chats as chatsApi } from '@/api';
import { useBackButton, useMainButton, useHapticFeedback } from '@/hooks';
import { badgeLabel } from '@/i18n/badges';
import { useAuthStore } from '@/store';
import type { SpecialistProfile } from '@/types/domain';
import { mockSpecialistProfile } from '@/mocks/specialists';
import { logger } from '@/utils/logger';
import { toast } from '@/components/atoms/Toast';
import styles from './pages.module.css';

export function SpecialistProfilePage(): JSX.Element {
  useBackButton();
  const haptic = useHapticFeedback();
  const navigate = useNavigate();
  const { slug = '' } = useParams<{ slug: string }>();
  const user = useAuthStore((s) => s.user);
  const [profile, setProfile] = useState<SpecialistProfile | null>(null);
  const [errored, setErrored] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setErrored(false);
    setProfile(null);
    setIsFavorite(false);
    (async () => {
      try {
        const data = await specialists.fetchSpecialist(slug);
        if (!cancelled) setProfile(data);
      } catch (err) {
        logger.warn('Profile fetch failed, using mock', err);
        if (!cancelled) {
          const mock = mockSpecialistProfile(slug);
          if (mock) setProfile(mock);
          else setErrored(true);
        }
      }
    })();
    // F-160 D2: hydrate the favourite state from the server so the heart
    // renders filled on revisit instead of always starting outline.
    (async () => {
      try {
        const data = await favorites.fetchFavorites();
        if (cancelled) return;
        const hit = data.results.some((f) => f.specialist.slug === slug);
        setIsFavorite(hit);
      } catch (err) {
        logger.warn('Favorites fetch failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const openChat = async (): Promise<void> => {
    haptic.impact('medium');
    try {
      const chat = await chatsApi.openChatWithSpecialist(slug);
      navigate(`/chats/${chat.id}`);
    } catch (err) {
      logger.warn('openChatWithSpecialist failed', err);
      toast.error('Не вдалось відкрити чат');
    }
  };

  useMainButton(
    profile
      ? {
          text: 'Написати',
          onClick: openChat,
        }
      : null,
  );

  if (errored && !profile) return <ErrorState onRetry={() => setProfile(null)} />;
  if (!profile)
    return (
      <Layout title="">
        <Spinner center />
      </Layout>
    );

  const toggleFavorite = async (): Promise<void> => {
    haptic.selectionChanged();
    try {
      if (isFavorite) {
        await favorites.removeFavorite(profile.slug);
        setIsFavorite(false);
        toast.info('Видалено з обраних');
      } else {
        await favorites.addFavorite(profile.slug);
        setIsFavorite(true);
        toast.success('Збережено в обрані');
      }
    } catch (err) {
      logger.warn('Favorite toggle failed', err);
      toast.error('Не вдалось зберегти');
    }
  };

  const primaryCat = profile.categories[0];
  // `/u/<slug>` almost always points at someone else's profile. When it
  // does, the Mini App `Header` (own avatar + bell) duplicates the real
  // hero a few pixels below — kill it and surface the favourite heart
  // inside the hero instead (NN/g H8).
  const isOwnProfile = !!user?.specialist_profile?.slug && user.specialist_profile.slug === profile.slug;
  // F-160 D2: filled vs outline heart reflects the favourite state.
  // Filled = `var(--destructive)` (red) with `fill="currentColor"`; outline
  // = default stroke. Toggle is bound to the favourites store via
  // `toggleFavorite` (which already emits a haptic selection-changed tap).
  const heartButton = (
    <button
      className={styles.heart}
      onClick={toggleFavorite}
      aria-label={isFavorite ? 'Прибрати з обраних' : 'В обрані'}
      aria-pressed={isFavorite}
      data-active={isFavorite ? 'true' : 'false'}
    >
      <Icon
        name="favorite"
        size={22}
        fill={isFavorite ? 'currentColor' : 'none'}
        color={isFavorite ? 'var(--destructive)' : undefined}
      />
    </button>
  );

  return (
    <Layout
      title=""
      hideHeader={!isOwnProfile}
      headerRight={isOwnProfile ? heartButton : undefined}
    >
      <section className={styles.profileHeroCompact}>
        <Avatar
          src={profile.avatar_url}
          name={profile.first_name}
          size={84}
          presence={profile.presence_status}
        />
        <div className={styles.profileHeroBody}>
          <h1 className={styles.profileHeroName}>{profile.first_name}</h1>
          {/* F-170 B3.1: specialist self-authored headline. Same visual
              scale as the name (slightly lighter weight) so the eye reads
              "Лілія" / "Перукар-стиліст" as a pair, IG-style. Hidden
              when empty so we don't render dead whitespace. */}
          {profile.title ? (
            <h2 className={styles.profileHeroTitle}>{profile.title}</h2>
          ) : null}
          <div className={styles.profileHeroMeta}>
            {primaryCat?.name_ua}
            {profile.city ? ` · ${profile.city.name_ua}` : ''}
          </div>
          {profile.is_test_persona && import.meta.env.DEV ? (
            // F-180 D6: dev-only — see SpecialistCard for rationale.
            <span className={styles.testPersonaPill} aria-label="Тестовий профіль">
              <Icon name="flask" size={14} />
              ТЕСТОВИЙ
            </span>
          ) : null}
          {(profile.badges?.length || profile.is_verified) ? (
            <div className={styles.profileBadgesRow}>
              {profile.is_verified ? <Badge variant="success">{badgeLabel('verified')}</Badge> : null}
              {profile.badges?.map((b) => (
                <Badge key={b} variant="accent">
                  {badgeLabel(b)}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>
        {isOwnProfile ? null : heartButton}
      </section>

      <ProfileFeed profile={profile} />

      {/* Web fallback: TG MainButton handles this in the WebView; on a plain
          browser there's nothing pinned, so render a sticky button at the bottom. */}
      {typeof window !== 'undefined' && !window.Telegram?.WebApp?.initData ? (
        <div className={styles.stickyCta}>
          <Button onClick={openChat} fullWidth>
            Написати
          </Button>
        </div>
      ) : null}
    </Layout>
  );
}
